import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import AddModeratorIcon from '@mui/icons-material/AddModerator';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import CancelIcon from '@mui/icons-material/Close';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import EditIcon from '@mui/icons-material/Edit';
import InsertLinkOutlinedIcon from '@mui/icons-material/InsertLinkOutlined';
import MonetizationOnOutlinedIcon from '@mui/icons-material/MonetizationOnOutlined';
import PersonPinOutlinedIcon from '@mui/icons-material/PersonPinOutlined';
import SaveIcon from '@mui/icons-material/Save';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import StackedBarChartOutlinedIcon from '@mui/icons-material/StackedBarChartOutlined';
import TextFormatOutlinedIcon from '@mui/icons-material/TextFormatOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import WatchLaterOutlinedIcon from '@mui/icons-material/WatchLaterOutlined';
import WavesOutlinedIcon from '@mui/icons-material/WavesOutlined';
import { Button, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  DataGridPremium as DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridColumnVisibilityModel,
  GridRenderCellParams,
  GridRowId,
  GridRowModel,
  GridRowModes,
  GridRowModesModel,
  GridRowsProp,
  GridToolbarContainer,
  GridValueFormatterParams,
  GridValueGetterParams,
} from '@mui/x-data-grid-premium';
import { formatCurrency } from 'helpers';
import {
  EstateBalanceSheetLiability,
  EstateBalanceSheetLiabilityAllocateTo,
  EstateBalanceSheetLiabilityCategory,
  EstateBalanceSheetLiabilityColumns,
  EstateBalanceSheetLiabilitySource,
  EstateBalanceSheetLiabilityStatus,
  FeatureSubscriptionType,
  useEstateBalanceSheetLiability_CreateEstateBalanceSheetLiabilityMutation,
  useEstateBalanceSheetLiability_DeleteEstateBalanceSheetLiabilityMutation,
  useEstateBalanceSheetLiability_GetEstateBalanceSheetLiabilitiesByEstateBalanceSheetIdQuery,
  useEstateBalanceSheetLiability_UpdateEstateBalanceSheetLiabilityMutation,
} from 'queries';

import Chip, { ChipDefaultColor } from 'common/chip';
import Modal from 'common/modal';
import Spinner from 'common/spinner';
import { canModifyBalanceSheet } from 'utils/feature-checker';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';

const useStyles = makeStyles(() => ({
  container: {
    color: '#1C2541',
  },
  title: {
    fontSize: '20px',
    fontWeight: 700,
    marginBottom: '24px',
  },
  dataGrid: {
    border: 0,
    borderRadius: '8px',
    '&.MuiDataGrid-root .MuiDataGrid-cell, &.MuiDataGrid-root .MuiDataGrid-columnHeader': {
      '&:focus, &:focus-within': {
        outline: 'none',
      },
    },
    '& .MuiDataGrid-columnHeadersInner': {
      backgroundColor: '#F9F9FB',
      borderRadius: '8px',
      borderBottom: 'none',
    },
    '& .MuiDataGrid-row': {
      border: '1px solid #ECEEF1',
    },
    '& .MuiDataGrid-cell': {
      border: '1px solid #ECEEF1',
    },
    '& .MuiDataGrid-columnHeaders': {
      borderBottom: 'none',
      borderTop: 'none',
    },
  },
  tableHeaderColumn: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
  },
  tableHeaderColumnLabel: {
    whiteSpace: 'initial',
    fontSize: '14px',
  },
  tableHeaderColumnIcon: {
    marginRight: '8px',
  },
}));

interface LiabilitiesGridRowModel extends GridRowModel<EstateBalanceSheetLiability> {
  isNew: boolean;
}

interface LiabilitiesGridSingleSelectOption {
  value?: string | null;
  label: string;
  color?: string;
}

const initialRows: LiabilitiesGridRowModel[] = [];

export const statusOptions: LiabilitiesGridSingleSelectOption[] = [
  { value: null, label: '' },
  {
    value: EstateBalanceSheetLiabilityStatus.Paid,
    label: 'Paid',
    color: ChipDefaultColor.PURPLE,
  },
  {
    value: EstateBalanceSheetLiabilityStatus.Unpaid,
    label: 'Unpaid',
    color: ChipDefaultColor.BLACK,
  },
];

export const categoryOptions: LiabilitiesGridSingleSelectOption[] = [
  { value: null, label: '' },
  {
    value: EstateBalanceSheetLiabilityCategory.AdministrationExpenseDisbursement,
    label: 'Administration Expense (Disbursement)',
    color: ChipDefaultColor.BLACK,
  },
  {
    value: EstateBalanceSheetLiabilityCategory.DebtsOfDecedentDisbursement,
    label: 'Debts of Decedent (Disbursement)',
    color: ChipDefaultColor.PURPLE,
  },
  {
    value: EstateBalanceSheetLiabilityCategory.FeesAndCommissionsDisbursement,
    label: 'Fees and Commissions (Disbursement)',
    color: ChipDefaultColor.YELLOW,
  },
  {
    value: EstateBalanceSheetLiabilityCategory.FuneralExpenseDisbursement,
    label: 'Funeral Expense (Disbursement)',
    color: ChipDefaultColor.GREEN,
  },
  {
    value: EstateBalanceSheetLiabilityCategory.TaxesDisbursement,
    label: 'Taxes (Disbursement)',
    color: ChipDefaultColor.BLACK,
  },
  {
    value: EstateBalanceSheetLiabilityCategory.Distribution,
    label: 'Distribution',
    color: ChipDefaultColor.BLACK,
  },
];

export const allocateToOptions: LiabilitiesGridSingleSelectOption[] = [
  { value: null, label: '' },
  {
    value: EstateBalanceSheetLiabilityAllocateTo.Income,
    label: 'Income',
    color: ChipDefaultColor.BLACK,
  },
  {
    value: EstateBalanceSheetLiabilityAllocateTo.Principal,
    label: 'Principal',
    color: ChipDefaultColor.PURPLE,
  },
];

export const sourceOptions: LiabilitiesGridSingleSelectOption[] = [
  {
    value: EstateBalanceSheetLiabilitySource.BankAccountDiscovery,
    label: 'Bank Account Discovery',
  },
  {
    value: EstateBalanceSheetLiabilitySource.DecisionVault,
    label: 'DecisionVault',
  },
  { value: EstateBalanceSheetLiabilitySource.EnteredByUser, label: 'Entered by User' },
  {
    value: EstateBalanceSheetLiabilitySource.LifeInsuranceDiscovery,
    label: 'Life Insurance Discovery',
  },
  { value: EstateBalanceSheetLiabilitySource.RetirementDiscovery, label: 'Retirement Discovery' },
  { value: EstateBalanceSheetLiabilitySource.TrusuiteSearch, label: 'Trusuite Search' },
];

interface EditToolbarProps {
  featureSubscriptionType: FeatureSubscriptionType;
  rows: LiabilitiesGridRowModel[];
  setRows: (newRows: (oldRows: GridRowsProp) => GridRowsProp) => void;
  setRowModesModel: (newModel: (oldModel: GridRowModesModel) => GridRowModesModel) => void;
}

function EditToolbar(props: EditToolbarProps) {
  const classes = useStyles();
  const { featureSubscriptionType, rows, setRows, setRowModesModel } = props;

  const handleClick = () => {
    if (!canModifyBalanceSheet(featureSubscriptionType)) {
      showToast({
        message: 'Please upgrade your account to add to the balance sheet.',
        severity: NotificationSeverity.WARNING,
      });
      return;
    }

    setRows((oldRows) => {
      const id = 'new-liability-row-id';

      setRowModesModel((oldModel) => ({
        ...oldModel,
        [id]: { mode: GridRowModes.Edit, fieldToFocus: 'name' },
      }));

      return [
        ...oldRows,
        {
          id,
          liabilityName: '',
          liabilityDescription: '',
          accountInvoiceNumber: undefined,
          balance: undefined,
          interestRate: undefined,
          dueDate: undefined,
          status: undefined,
          paidBy: undefined,
          isDeductibleExpense: false,
          category: undefined,
          allocateTo: undefined,
          notes: undefined,
          source: EstateBalanceSheetLiabilitySource.EnteredByUser,
          isNew: true,
        },
      ];
    });
  };

  return (
    <GridToolbarContainer>
      <Button
        color="primary"
        startIcon={<AddIcon />}
        onClick={handleClick}
        disabled={!!rows.find((row) => row.isNew)}
        className={classes.container}
      >
        Add new
      </Button>
    </GridToolbarContainer>
  );
}

type Props = {
  featureSubscriptionType: FeatureSubscriptionType;
  hiddenLiabilityColumns: EstateBalanceSheetLiabilityColumns[];
  estateBalanceSheetId: string;
  onDataLoaded: (data: EstateBalanceSheetLiability[]) => void;
};

export default function EstateBalanceSheetLiabilities({
  featureSubscriptionType,
  hiddenLiabilityColumns,
  estateBalanceSheetId,
  onDataLoaded,
}: Props) {
  const classes = useStyles();

  const { portalId } = useParams<Record<string, string>>();

  const [rows, setRows] = useState<LiabilitiesGridRowModel[]>(initialRows);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [selectedRowToDelete, setSelectedRowToDelete] = useState<GridRowId>();

  const { data: estateBalanceSheetLiabilitiesData, loading: estateBalanceSheetLiabilitiesLoading } =
    useEstateBalanceSheetLiability_GetEstateBalanceSheetLiabilitiesByEstateBalanceSheetIdQuery({
      variables: { input: { estateBalanceSheetId, portalId } },
      onError: (e) =>
        showAlert({
          message: 'Error getting balance sheet liabilities by balance sheet ID: ' + e.message,
          severity: NotificationSeverity.ERROR,
        }),
    });

  const [createEstateBalanceSheetLiability] =
    useEstateBalanceSheetLiability_CreateEstateBalanceSheetLiabilityMutation({
      onError: (e) =>
        showAlert({
          message: 'Error creating new balance sheet liability: ' + e.message,
          severity: NotificationSeverity.ERROR,
        }),
    });

  const [updateEstateBalanceSheetLiability] =
    useEstateBalanceSheetLiability_UpdateEstateBalanceSheetLiabilityMutation({
      onError: (e) =>
        showAlert({
          message: 'Error updating balance sheet liability: ' + e.message,
          severity: NotificationSeverity.ERROR,
        }),
    });

  const [deleteEstateBalanceSheetLiability] =
    useEstateBalanceSheetLiability_DeleteEstateBalanceSheetLiabilityMutation({
      onError: (e) =>
        showAlert({
          message: 'Error deleting balance sheet liability: ' + e.message,
          severity: NotificationSeverity.ERROR,
        }),
    });

  useEffect(() => {
    if (rows.length === 0 && estateBalanceSheetLiabilitiesData?.data) {
      setRows(
        estateBalanceSheetLiabilitiesData.data.map((liability) => {
          return { ...liability, isNew: false };
        }),
      );
    }
  }, [estateBalanceSheetLiabilitiesData, rows.length]);

  useEffect(() => {
    if (estateBalanceSheetLiabilitiesData?.data) {
      onDataLoaded(
        estateBalanceSheetLiabilitiesData.data.map((liability) => ({
          ...liability,
          isNew: false,
        })),
      );
    }
  }, [estateBalanceSheetLiabilitiesData, onDataLoaded, rows]);

  if (estateBalanceSheetLiabilitiesLoading || !estateBalanceSheetLiabilitiesData) {
    return <Spinner />;
  }

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = () => {
    const rowToDelete = rows.find((row) => row.id === selectedRowToDelete);
    if (rowToDelete && !rowToDelete.isNew) {
      deleteEstateBalanceSheetLiability({
        onCompleted: () => {
          setRows(rows.filter((row) => row.id !== rowToDelete.id));
        },
        variables: { input: { id: rowToDelete.id, portalId } },
      });
    } else {
      setRows(rows.filter((row) => row.id !== selectedRowToDelete));
    }
    setSelectedRowToDelete(undefined);
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows.find((row) => row.id === id);
    if (editedRow!.isNew) {
      setRows(rows.filter((row) => row.id !== id));
    }
  };

  const processRowUpdate = (newRow: LiabilitiesGridRowModel) => {
    const updatedRow = { ...newRow, isNew: false };

    const {
      id,
      liabilityName,
      liabilityDescription,
      accountInvoiceNumber,
      balance,
      interestRate,
      dueDate,
      status,
      paidBy,
      isDeductibleExpense,
      category,
      allocateTo,
      notes,
      source,
    } = newRow;

    if (newRow.isNew) {
      createEstateBalanceSheetLiability({
        onCompleted: ({ data: newEstateBalanceSheetLiability }) => {
          if (newEstateBalanceSheetLiability) {
            const updatedRows = rows.map((row) =>
              row.id === newRow.id ? { ...newEstateBalanceSheetLiability, isNew: false } : row,
            );

            setRows(updatedRows);
            onDataLoaded(updatedRows);
          }
        },
        variables: {
          input: {
            estateBalanceSheetId,
            portalId,
            liabilityName,
            liabilityDescription,
            accountInvoiceNumber,
            balance,
            interestRate,
            dueDate,
            status,
            paidBy,
            isDeductibleExpense,
            category,
            allocateTo,
            notes,
            source,
          },
        },
      });
    } else {
      updateEstateBalanceSheetLiability({
        onCompleted: ({ data: newEstateBalanceSheetLiability }) => {
          setRows(
            rows.map((row) =>
              row.id === newRow.id ? { ...newEstateBalanceSheetLiability, isNew: false } : row,
            ),
          );
        },
        variables: {
          input: {
            id,
            portalId,
            liabilityName,
            liabilityDescription,
            accountInvoiceNumber,
            balance,
            interestRate,
            dueDate,
            status,
            paidBy,
            isDeductibleExpense,
            category,
            allocateTo,
            notes,
            source,
          },
        },
      });
    }
    return updatedRow;
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const columns: GridColDef[] = [
    {
      field: EstateBalanceSheetLiabilityColumns.LiabilityName,
      minWidth: 150,
      editable: true,
      renderHeader: () => (
        <div className={classes.tableHeaderColumn}>
          <CategoryOutlinedIcon className={classes.tableHeaderColumnIcon} fontSize="small" />
          <Typography className={classes.tableHeaderColumnLabel}>Name</Typography>
        </div>
      ),
    },
    {
      field: EstateBalanceSheetLiabilityColumns.LiabilityDescription,
      minWidth: 200,
      editable: true,
      renderHeader: () => (
        <div className={classes.tableHeaderColumn}>
          <TextFormatOutlinedIcon className={classes.tableHeaderColumnIcon} fontSize="small" />
          <Typography className={classes.tableHeaderColumnLabel}>
            Liability Description (Company)
          </Typography>
        </div>
      ),
    },
    {
      field: EstateBalanceSheetLiabilityColumns.AccountInvoiceNumber,
      minWidth: 200,
      editable: true,
      renderHeader: () => (
        <div className={classes.tableHeaderColumn}>
          <PersonPinOutlinedIcon className={classes.tableHeaderColumnIcon} fontSize="small" />
          <Typography className={classes.tableHeaderColumnLabel}>Account # / Invoice #</Typography>
        </div>
      ),
    },
    {
      field: EstateBalanceSheetLiabilityColumns.Balance,
      minWidth: 150,
      editable: true,
      type: 'number',
      renderHeader: () => (
        <div className={classes.tableHeaderColumn}>
          <MonetizationOnOutlinedIcon className={classes.tableHeaderColumnIcon} fontSize="small" />
          <Typography className={classes.tableHeaderColumnLabel}>Balance</Typography>
        </div>
      ),
      valueFormatter: ({ value }: GridValueFormatterParams) =>
        value ? formatCurrency(`${value * 100.0}`) : undefined,
    },
    {
      field: EstateBalanceSheetLiabilityColumns.DueDate,
      minWidth: 150,
      editable: true,
      type: 'date',
      renderHeader: () => (
        <div className={classes.tableHeaderColumn}>
          <WatchLaterOutlinedIcon className={classes.tableHeaderColumnIcon} fontSize="small" />
          <Typography className={classes.tableHeaderColumnLabel}>Due Date</Typography>
        </div>
      ),
      valueGetter: (params: GridValueGetterParams) => {
        const row: EstateBalanceSheetLiability = params.row;
        return row.dueDate ? new Date(row.dueDate) : undefined;
      },
    },
    {
      field: EstateBalanceSheetLiabilityColumns.InterestRate,
      minWidth: 150,
      editable: true,
      type: 'number',
      renderHeader: () => (
        <div className={classes.tableHeaderColumn}>
          <StackedBarChartOutlinedIcon className={classes.tableHeaderColumnIcon} fontSize="small" />
          <Typography className={classes.tableHeaderColumnLabel}>Interest Rate</Typography>
        </div>
      ),
      valueFormatter: ({ value }: GridValueFormatterParams) => (value ? `${value}%` : undefined),
    },
    {
      field: EstateBalanceSheetLiabilityColumns.Status,
      minWidth: 150,
      editable: true,
      renderHeader: () => (
        <div className={classes.tableHeaderColumn}>
          <WatchLaterOutlinedIcon className={classes.tableHeaderColumnIcon} fontSize="small" />
          <Typography className={classes.tableHeaderColumnLabel}>Status</Typography>
        </div>
      ),
      renderCell: (params: GridRenderCellParams) => {
        const value: string = params.value;
        const colDef: any = params.colDef;
        const valueOption: LiabilitiesGridSingleSelectOption | undefined = colDef.valueOptions.find(
          (valueOption: LiabilitiesGridSingleSelectOption) => valueOption.value === value,
        );
        if (!value) {
          return <></>;
        } else if (valueOption) {
          return <Chip label={valueOption.label} color={valueOption.color} />;
        } else {
          return <Chip label={value} />;
        }
      },
      type: 'singleSelect',
      valueOptions: statusOptions,
    },
    {
      field: EstateBalanceSheetLiabilityColumns.PaidBy,
      minWidth: 150,
      editable: true,
      renderHeader: () => (
        <div className={classes.tableHeaderColumn}>
          <VerifiedUserOutlinedIcon className={classes.tableHeaderColumnIcon} fontSize="small" />
          <Typography className={classes.tableHeaderColumnLabel}>Paid By, if Paid</Typography>
        </div>
      ),
    },
    {
      field: EstateBalanceSheetLiabilityColumns.IsDeductibleExpense,
      minWidth: 300,
      editable: true,
      renderHeader: () => (
        <div className={classes.tableHeaderColumn}>
          <VerifiedUserOutlinedIcon className={classes.tableHeaderColumnIcon} fontSize="small" />
          <Typography className={classes.tableHeaderColumnLabel}>Deductible expense?</Typography>
        </div>
      ),
      renderCell: (params: GridRenderCellParams) => {
        const value: string = params.value;
        const colDef: any = params.colDef;
        const valueOption: LiabilitiesGridSingleSelectOption | undefined = colDef.valueOptions.find(
          (valueOption: LiabilitiesGridSingleSelectOption) => valueOption.value === value,
        );
        if (!valueOption && value === undefined) {
          return <></>;
        } else if (valueOption) {
          return <Chip label={valueOption.label} color={valueOption.color} />;
        } else {
          return <Chip label={value} />;
        }
      },
      type: 'singleSelect',
      valueOptions: [
        { value: true, label: 'Yes', color: ChipDefaultColor.PURPLE },
        { value: false, label: 'No', color: ChipDefaultColor.BLACK },
      ],
    },
    {
      field: EstateBalanceSheetLiabilityColumns.Category,
      minWidth: 270,
      editable: true,
      renderHeader: () => (
        <div className={classes.tableHeaderColumn}>
          <WavesOutlinedIcon className={classes.tableHeaderColumnIcon} fontSize="small" />
          <Typography className={classes.tableHeaderColumnLabel}>Category</Typography>
        </div>
      ),
      renderCell: (params: GridRenderCellParams) => {
        const value: string = params.value;
        const colDef: any = params.colDef;
        const valueOption: LiabilitiesGridSingleSelectOption | undefined = colDef.valueOptions.find(
          (valueOption: LiabilitiesGridSingleSelectOption) => valueOption.value === value,
        );
        if (!value) {
          return <></>;
        } else if (valueOption) {
          return <Chip label={valueOption.label} color={valueOption.color} />;
        } else {
          return <Chip label={value} />;
        }
      },
      type: 'singleSelect',
      valueOptions: categoryOptions,
    },
    {
      field: EstateBalanceSheetLiabilityColumns.AllocateTo,
      minWidth: 150,
      editable: true,
      renderHeader: () => (
        <div className={classes.tableHeaderColumn}>
          <AddModeratorIcon className={classes.tableHeaderColumnIcon} fontSize="small" />
          <Typography className={classes.tableHeaderColumnLabel}>Allocate To</Typography>
        </div>
      ),
      renderCell: (params: GridRenderCellParams) => {
        const value: string = params.value;
        const colDef: any = params.colDef;
        const valueOption: LiabilitiesGridSingleSelectOption | undefined = colDef.valueOptions.find(
          (valueOption: LiabilitiesGridSingleSelectOption) => valueOption.value === value,
        );
        if (!value) {
          return <></>;
        } else if (valueOption) {
          return <Chip label={valueOption.label} color={valueOption.color} />;
        } else {
          return <Chip label={value} />;
        }
      },
      type: 'singleSelect',
      valueOptions: allocateToOptions,
    },
    {
      field: EstateBalanceSheetLiabilityColumns.Notes,
      minWidth: 200,
      editable: true,
      renderHeader: () => (
        <div className={classes.tableHeaderColumn}>
          <CreateOutlinedIcon className={classes.tableHeaderColumnIcon} fontSize="small" />
          <Typography className={classes.tableHeaderColumnLabel}>Comment / Notes</Typography>
        </div>
      ),
    },
    {
      field: EstateBalanceSheetLiabilityColumns.Source,
      minWidth: 150,
      editable: true,
      renderHeader: () => (
        <div className={classes.tableHeaderColumn}>
          <InsertLinkOutlinedIcon className={classes.tableHeaderColumnIcon} fontSize="small" />
          <Typography className={classes.tableHeaderColumnLabel}>Source</Typography>
        </div>
      ),
      type: 'singleSelect',
      valueOptions: sourceOptions,
    },
    {
      field: EstateBalanceSheetLiabilityColumns.Actions,
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      cellClassName: 'actions',
      renderHeader: () => (
        <div className={classes.tableHeaderColumn}>
          <SettingsOutlinedIcon className={classes.tableHeaderColumnIcon} fontSize="small" />
          <Typography className={classes.tableHeaderColumnLabel}>Actions</Typography>
        </div>
      ),
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Save"
              sx={{
                color: 'primary.main',
              }}
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />,
          ];
        }

        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={() => setSelectedRowToDelete(id)}
            color="inherit"
          />,
        ];
      },
    },
  ];

  const columnVisibilityModel = hiddenLiabilityColumns.reduce(
    (acc, column) => ({ ...acc, [column]: false }),
    {},
  ) as GridColumnVisibilityModel;

  return (
    <div className={classes.container}>
      <Modal
        setShowModal={() => setSelectedRowToDelete(undefined)}
        onActionClick={handleDeleteClick}
        onExitClick={() => setSelectedRowToDelete(undefined)}
        showModal={Boolean(selectedRowToDelete)}
        title=""
        content={<Typography>Are you sure? This will permanently delete this row.</Typography>}
        actionVerb="Delete"
      />
      <Typography variant="h1" className={classes.title}>
        Liabilities
      </Typography>
      <DataGrid
        className={classes.dataGrid}
        rows={rows}
        columns={columns}
        editMode="row"
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        processRowUpdate={processRowUpdate}
        getRowHeight={() => 'auto'}
        autoHeight={true}
        columnVisibilityModel={columnVisibilityModel}
        slots={{
          toolbar: (props) => (
            <EditToolbar featureSubscriptionType={featureSubscriptionType} rows={rows} {...props} />
          ),
        }}
        slotProps={{
          toolbar: { setRows, setRowModesModel },
        }}
      />
    </div>
  );
}
